/**
 * The language selector, as items for the profile menu — the same place and
 * the same behaviour the product gives it.
 *
 * Choosing one navigates to the same page under the other prefix first,
 * because the URL is what decides the page's language, and then stores the
 * preference, which only decides where a language-less URL redirects. The
 * store is fire-and-forget: a failed write leaves the administrator reading
 * the language they chose, which is right.
 *
 * Its own copy rather than the product's hook: the two applications do not
 * import each other's source, and each has its own generated operations.
 */
import { useMutation } from '@apollo/client/react'
import { toLanguageCode } from '@athena/i18n'
import { useLanguage, useTranslate, type ProfileMenuItem } from '@athena/ui'
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router'

import {
  AdminUpdatePreferredLanguageDocument,
  AdminViewerDocument,
} from '../graphql/generated/operations.js'
import { LANGUAGES, withLanguage, type Language } from '../lib/language.js'

/** Where `navigate` will accept a `to`. Derived, so a route rename reaches it. */
type Destination = Parameters<ReturnType<typeof useNavigate>>[0] extends { to?: infer T }
  ? T
  : never

export function useLanguageMenuItems(): ProfileMenuItem[] {
  const t = useTranslate()
  const current = useLanguage()
  const navigate = useNavigate()
  const [store] = useMutation(AdminUpdatePreferredLanguageDocument, {
    refetchQueries: [AdminViewerDocument],
  })

  // The route as a template plus its params, so switching cannot rewrite a
  // parameter that happens to spell a language.
  const route = useRouterState({ select: (state) => state.matches.at(-1)?.fullPath })
  const target = (route === '/$language/' ? '/$language' : route) as Destination
  const params = useParams({ strict: false })
  const search = useRouterState({ select: (state) => state.location.search })
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  function choose(language: Language) {
    if (language === current) return
    if (target) {
      void navigate({ to: target, params: { ...params, language }, search, replace: true })
    } else {
      void navigate({ href: withLanguage(pathname, language), replace: true })
    }
    void store({ variables: { language: toLanguageCode(language) } })
  }

  return LANGUAGES.map((language) => ({
    id: `language-${language}`,
    group: 'language',
    label: t(`language.${language}`),
    selected: language === current,
    onSelect: () => choose(language),
  }))
}
